import { Fragment, ReactNode, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'

import { Commodity } from './products'
import { TriState } from './MappingState'
import { linkDifference } from '../BackendProvider'
import { usePrompt } from '../components/blocker'
import { IconSpinner } from '../components/icons'

export type ProductMappingMatrixProps<F, L> = {
    pageTitle: string
    heading: string

    families: F[]
    commodities: Commodity[]
    links: L[]

    getFamilyId: (family: F) => number | undefined
    renderFamilyHeader: (family: F) => ReactNode
    renderCommodityCell: (commodity: Commodity) => ReactNode

    baseLinkFor: (commodityId: number, family: F) => Partial<L>
    isEqualLink: (a: Partial<L>, b: Partial<L>) => boolean

    postLinks: (links: L[]) => Promise<unknown>
    deleteLinks: (links: L[]) => Promise<unknown>

    headerExtras?: ReactNode
    legend?: ReactNode
}

export function ProductMappingMatrix<F, L>({
    pageTitle, heading,
    families, commodities, links: serverLinks,
    getFamilyId, renderFamilyHeader, renderCommodityCell,
    baseLinkFor, isEqualLink,
    postLinks, deleteLinks,
    headerExtras, legend,
}: ProductMappingMatrixProps<F, L>) {
    const { t } = useTranslation()

    const [links, setLinks] = useState<L[]>(serverLinks)
    const [isEditing, setIsEditing] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    usePrompt(t('leave-site-changes'), isEditing)

    const commoditiesByClass: [string, Commodity[]][] = _(commodities)
        .groupBy(c => c.commodityClass)
        .mapValues(cs => _.sortBy(cs, c => c.commodityReference.toLowerCase()))
        .toPairs()
        .sortBy(([key]) => key)
        .value()

    async function saveDiff() {
        setIsSubmitting(true)
        const [toAdd, toDelete] = linkDifference(serverLinks, links)

        if (toDelete.length > 0)
            await deleteLinks(toDelete)
        if (toAdd.length > 0)
            await postLinks(toAdd)

        setIsEditing(false)
        setIsSubmitting(false)
    }

    function cancel() {
        setLinks(serverLinks)
        setIsEditing(false)
    }

    return <>
        {/* @ts-ignore */}
        <Helmet>
            <title>{pageTitle}</title>
        </Helmet>
        <div className="header-row">
            <div className="flex flex-row items-center max-w-full gap-4">
                <h2 className="modern-h2 grow">{heading}</h2>
                {isEditing
                    ? <div className="flex flex-row items-center gap-2">
                        <button className="text-sm btn-primary" onClick={() => saveDiff()}>
                            {t('save')} {isSubmitting && <IconSpinner className="size-4 animate-spin inline ml-1" />}
                        </button>
                        <button className="text-sm btn-secondary" onClick={() => cancel()}>{t('cancel')}</button>
                    </div>
                    : <button className="text-sm btn-secondary" onClick={() => setIsEditing(true)}>{t('edit')}</button>}
                {headerExtras}
            </div>
        </div>
        <div className="main-content grow">
            <table className="relative w-fit border-collapse">
                <thead>
                    <tr className="sticky -top-4 z-20 shadow-[0_2px_2px_0_rgb(0_0_0_/_0.1)] text-left">
                        <th className="w-full h-[52px] pb-px bg-white dark:bg-pcx-100">
                            <div className="sticky -top-4 -left-4 z-40 w-full h-full bg-white dark:bg-pcx-100" />
                        </th>
                        {families.map(family =>
                            <th key={'f-' + getFamilyId(family)} className="py-px bg-white dark:bg-pcx-100 font-normal max-w-[8rem]">
                                <div className="bg-white dark:bg-pcx-100 px-2 pb-2">
                                    {renderFamilyHeader(family)}
                                </div>
                            </th>)}
                    </tr>
                </thead>
                <tbody>
                    {commoditiesByClass.map(([commodityClass, perClass]) =>
                        <Fragment key={'frag-' + commodityClass}>
                            <tr key={'cl-' + commodityClass} className="border-t-2 border-pcx-300">
                                <td className="text-pcx-500/80 font-medium pl-1 sticky -left-4">
                                    <div className="bg-white dark:bg-pcx-100 whitespace-nowrap">
                                        {commodityClass !== "undefined" ? commodityClass : t("unclassified")}
                                    </div>
                                </td>
                                {families.map((f, fi) => <td key={`${commodityClass}-${fi}`} />)}
                            </tr>
                            {perClass.map(commodity =>
                                <tr key={'c-' + commodity.commodityId} className="border-t border-pcx-200">
                                    <td className="sticky -left-4 py-0.5">
                                        {renderCommodityCell(commodity)}
                                    </td>
                                    {families.map(family =>
                                        <td key={`c-f-${commodity.commodityId}-${getFamilyId(family)}`} className="px-2 text-center">
                                            <TriState
                                                links={links}
                                                setLinks={setLinks}
                                                baseLink={baseLinkFor(commodity.commodityId, family)}
                                                disabled={!isEditing}
                                                isEqualLink={isEqualLink}
                                                isThirdParty={commodity.isThirdParty}
                                            />
                                        </td>)}
                                </tr>)}
                        </Fragment>
                    )}
                </tbody>
            </table>
            {legend}
        </div>
    </>
}
